import React, { useCallback, useEffect } from "react";
import { WizardService } from "../../../services/WizardService";
import { Sdk } from "../../../types/sdk";
import {
  Breadcrumb,
  Button,
  Drawer,
  Layout,
  message,
  Descriptions,
  Row,
  Skeleton,
  Col,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Plugin } from "../../../types/plugin";
import { PluginForm } from "./PluginForm";
import { AppRoutes } from "../../../AppRoutes";
import { DropdownAction } from "../../components/ColumnBuilder";
import { SdkList } from "../sdk/SdkList";
import { ResponsiveCard } from "../../components/ResponsiveCard";
import { SuggestedSdkList } from "../sdk/SuggestedSdkList";
import { IntegrationPreview } from "../../components/IntegrationPreview";

const { Content } = Layout;

export const PluginViewPage = () => {
  const api = WizardService.Plugin;
  const navigate = useNavigate();

  const { id } = useParams();

  const [isPluginLoading, setPluginLoading] = React.useState<boolean>(false);
  const [isPluginEditing, setPluginEditing] = React.useState<boolean>(false);
  const [plugin, setPlugin] = React.useState<Plugin>();

  // Starts `true` (not `false`): `sdks` starts as `[]` before the fetch effect below ever runs,
  // so an initial `false` here would let the very first render read as "genuinely no SDK release"
  // and flash the "No SDK release available" alert for one frame.
  const [isSdksLoading, setSdksLoading] = React.useState<boolean>(true);
  const [isAddingSdk, setAddingSdk] = React.useState<boolean>(false);
  const [sdks, setSdks] = React.useState<Sdk[]>([]);

  const savePlugin = (record: Plugin) => {
    setPluginLoading(true);
    let promise: Promise<any>;
    if (record.id !== undefined && record.id > 0) {
      promise = api.update(record.id, record);
    } else {
      promise = api.create(record);
    }

    promise
      .then((_) => fetch())
      .catch((error) => message.error(error.message))
      .finally(() => {
        setPluginEditing(false);
        setPluginLoading(false);
      });
  };

  const deleteSdk = (sdkId: number) => {
    setSdksLoading(true);
    api
      .removeSdk(parseInt(id!), sdkId)
      .then(() => fetchSdks())
      .catch((error) => {
        message.error(error.message);
        setSdksLoading(false);
      });
  };

  const addSdk = (records: Sdk[]) => {
    setSdksLoading(true);
    Promise.all(records.map((record) => api.addSdk(parseInt(id!), record)))
      .then(() => fetchSdks())
      .catch((error) => {
        message.error(error.message);
        setSdksLoading(false);
      });
  };

  // Callbacks
  const fetch = useCallback(() => {
    setPluginLoading(true);
    api
      .one(parseInt(id!))
      .then((sdk) => setPlugin(sdk))
      .catch((error) => message.error(error.message))
      .finally(() => setPluginLoading(false));
  }, [api, id]);

  const fetchSdks = useCallback(() => {
    setSdksLoading(true);
    api
      .sdks(parseInt(id!))
      .then((plugins) => setSdks(plugins || []))
      .catch((error) => message.error(error.message))
      .finally(() => setSdksLoading(false));
  }, [api, id]);

  // Hooks
  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    fetchSdks();
  }, [fetchSdks]);

  const formattedDate = (date?: string) => {
    if (date) {
      return new Date(date).toDateString();
    }
  };

  return (
    <Layout>
      <Content>
        <Row style={{ margin: "1rem 0" }}>
          <Breadcrumb
            items={[
              {
                href: "/" + AppRoutes.Wizard.PluginList,
                title: "Plugin Releases",
                onClick: () => {
                  navigate(-1);
                },
              },
              {
                title: "Plugin #" + id,
              },
            ]}
          />
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <ResponsiveCard
              title="Plugin"
              contentPadding="1rem"
              extra={
                <Button
                  type="text"
                  icon={<EditOutlined  />}
                  onClick={() => setPluginEditing(true)}
                />
              }
            >
              {isPluginLoading ? (
                <Skeleton active />
              ) : (
                <Descriptions
                  column={2}
                  items={[
                    {
                      key: "1",
                      label: "Framework",
                      children: plugin?.framework.toLocaleUpperCase(),
                    },
                    { key: "3", label: "Status", children: plugin?.status },
                    { key: "4", label: "Version", children: plugin?.version },
                    {
                      key: "5",
                      label: "Created",
                      children: formattedDate(plugin?.createdAt),
                    },
                    {
                      key: "6",
                      label: "Updated",
                      children: formattedDate(plugin?.updatedAt),
                    },
                  ]}
                />
              )}
            </ResponsiveCard>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <ResponsiveCard
              title={"Native SDK"}
              extra={
                <Button
                  type="text"
                  icon={<PlusOutlined  />}
                  onClick={() => setAddingSdk(true)}
                />
              }
            >
              <SdkList
                loading={isSdksLoading}
                records={sdks}
                actions={[DropdownAction.VIEW, DropdownAction.DELETE]}
                removeRecord={deleteSdk}
                viewRecord={(id) =>
                  navigate(
                    "/" + AppRoutes.Wizard.Sdk.replace(":id", id.toString())
                  )
                }
                pagination={false}
                editable
              />
            </ResponsiveCard>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <ResponsiveCard title={"Integration Preview"} contentPadding="1rem">
              {/* No cache-busting `key` needed here: IntegrationPreview's own fetch chain is keyed
                  off the anchor Sdk's version/status (derived fresh from `sdks` every render), not
                  off the legacy `adapters`/`sdks` CRUD list the old PluginIntegrationPreview
                  depended on. */}
              {/* The Unity tab also depends on `plugin?.version`, so the "no release yet" alert
                  must stay suppressed while either fetch is still in flight, not just `sdks`'. */}
              <IntegrationPreview
                plugin={plugin}
                sdks={sdks}
                sdksLoading={isSdksLoading || isPluginLoading}
              />
            </ResponsiveCard>
          </Col>
        </Row>
        <SuggestedSdkList
          pluginId={plugin?.id}
          open={isAddingSdk}
          onSdksSelected={(records) => addSdk(records)}
          onClose={() => setAddingSdk(false)}
        />
        <Drawer
          title={"Edit Plugin Release #" + id}
          width={600}
          onClose={(_) => setPluginEditing(false)}
          open={isPluginEditing}
          styles={{
            body: {
              paddingBottom: 80,
            },
          }}
        >
          <PluginForm
            plugin={plugin}
            isLoading={isPluginLoading}
            onFinish={savePlugin}
          />
        </Drawer>
      </Content>
    </Layout>
  );
};
